import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import PatternFill from 'highcharts/modules/pattern-fill';
import * as moment from 'moment-timezone';

PatternFill(Highcharts);

/** Rojo de alerta de la gráfica: línea del mínimo y borde de "bajo el mínimo". */
const MIN_RED = '#cd2120';

/**
 * Una estación + producto listos para graficar. Quien abre el modal (Análisis de
 * Pedido, Sugerencias de la semana) traduce SUS datos a esta forma; el modal no
 * conoce de dónde salen. Día 0 = mañana.
 */
/** Clic en una ficha de pipa bajo la fecha (Edey 15-oct): quien aloja la gráfica abre
 *  el mismo modal que en el Tablero (detalle de la pipa, pedido pendiente o pedir la
 *  sugerida). `pastOffset` = días antes de hoy (0 = hoy) para días pasados; `dayIndex`
 *  = índice del día proyectado. */
export interface ProjectionChipClick {
  kind: 'arrived' | 'confirmed' | 'pending' | 'suggested';
  stationId: string;
  product: string;
  pastOffset: number | null;
  dayIndex: number | null;
  liters: number;
}

export interface ProjectionRow {
  stationId: string;
  stationName: string;
  product: string; // interno GASPRE: regular | premium | diesel
  productLabel: string; // comercial MOBICO: Extra | Supreme | Diésel
  stockToday: number | null;
  /** Días pasados (los mismos del calendario: la semana en curso y, si está
   *  desplegada, la anterior): cierre y, si hubo, la pipa que LLEGÓ ese día. */
  past?: { label: string; value: number | null; arrived?: number | null; inProcess?: number | null; prevWeek?: boolean; offset?: number }[];
  /** Pipa que llegó HOY (past_purchase_0). */
  todayArrived?: number | null;
  /** Pipa de hoy aún sin entrega confirmada (revisión 12-oct): chip «En proceso». */
  todayInProcess?: number | null;
  days: { dow: string; name: string }[];
  /** Cuántos de `days` caben en las dos semanas que se dibujan cerradas (hasta el
   *  domingo de la semana siguiente); los demás son la tercera semana, que se abre
   *  con el botón «Tercera semana» (19-oct). Sin él se dibujan todos. */
  twoWeekDays?: number;
  /** Inventario con el que cierra cada día, YA con las pipas de ese día. */
  closes: (number | null)[];
  confirmed: number[];
  pending: number[];
  suggested: number[];
  min: number;
  tope: number | null;
  /** Inventario objetivo (Obj. %) en litros; null si la estación no lo capturó. */
  target?: number | null;
  ruleDays: number | null;
  /** Nivel de la Regla de días en litros, tal como lo mide el motor (promedio de la venta pronosticada de los N días que siguen); sin él, N × venta base. */
  ruleLevel?: number | null;
  /** Manda el porcentaje objetivo (Obj. %) y no la Regla: la gráfica no dibuja la línea de la Regla de días. */
  objectiveRules?: boolean;
  /** Días sobre el mínimo con los que el motor hace llegar la pipa (acoplados a la pipa usual). */
  arrivalDays?: number | null;
  ruleMode?: string;
  cappedByPipe?: boolean;
  typicalPipe?: number | null;
  sale: number | null;
  daysLeft: number | null;
}

/**
 * Modal "Inventario proyectado" (mock MOBICO 07/08-oct, lámina 03): misma
 * anatomía que "Pedido pendiente" (header verde, cuerpo, botón). Gráfica con la
 * gramática de Lealtad: columnas desde 0, grid punteado, etiquetas grises,
 * leyenda FUERA del SVG. Una sola serie: con cuánto cierra cada día (Antier · Ayer ·
 * Hoy reales + proyección); las pipas van como chip bajo la fecha, no apiladas
 * (revisión 08-oct: "mostrar nivel de inventario siempre, y aparte cuándo entra
 * la pipa"). Líneas de mínimo, umbral de la regla y tope.
 */
/** Fuera del flujo MOBICO: colorsProducts de GASPRE (gasprecio.service) como base,
 *  con su tinte claro para el teórico. */
const GASPRE_PRODUCT_PALETTE: Record<string, { base: string; light: string; faded: string; pend: string }> = {
  regular: { base: '#179446', light: '#dcefe3', faded: 'rgba(23,148,70,.35)', pend: '#179446' },
  premium: { base: '#CD2120', light: '#f8dddd', faded: 'rgba(205,33,32,.35)', pend: '#CD2120' },
  diesel: { base: '#1E2228', light: '#e0e2e4', faded: 'rgba(30,34,40,.35)', pend: '#1E2228' },
};

@Component({
  selector: 'app-inventory-projection',
  templateUrl: './inventory-projection.component.html',
  styleUrls: ['./inventory-projection.component.scss']
})
export class InventoryProjectionComponent implements OnInit, OnChanges {
  /** Candidatas (una por estación + producto). */
  @Input() rows: ProjectionRow[] = [];
  /** Con qué abrir. */
  @Input() stationId = '';
  @Input() product = '';
  /** Tablero: selector de estación con buscador. Vista de estación: solo la suya. */
  @Input() stationSelector = true;
  /** Vista de estación: la gráfica va EN LA PÁGINA (sin ventana ni «Cerrar») y el
   *  producto se elige con el control segmentado del mock v3. */
  @Input() inline = false;
  /** Flujo de pipas de MOBICO: paleta del cliente, estados de pedido y regla.
   *  En false (Divitsa, 26-oct) usa los colores de producto de GASPRE y solo
   *  muestra inventario real y teórico. */
  @Input() mobicoFlow = true;
  @Output() closed = new EventEmitter<void>();
  @Output() chipClick = new EventEmitter<ProjectionChipClick>();
  /** Días pasados pintados (con su offset) y cuántos son: mapean el índice del eje. */
  private axisPast: { offset: number }[] = [];

  Highcharts: typeof Highcharts = Highcharts;
  options: Highcharts.Options = {};
  view: any = null;

  stationOptions: { value: string; label: string }[] = [];
  productOptions: { value: string; label: string }[] = [];
  station = '';
  selectedProduct = '';
  /** Estación cuyos productos están en productOptions: el select de producto
   *  solo se reconstruye al cambiar de estación (reconstruirlo en cada cambio
   *  de producto re-creaba las opciones y el select mostraba la anterior). */
  private optionsStation = '';
  /** Semana anterior desplegada (control propio de la gráfica, como el del
   *  calendario; Edey 09-oct). Cerrada por default. */
  showPrevWeek = false;
  /** Tercera semana desplegada (jefe 19-oct, «dos semanas y media»): cerrada por
   *  default, se abre con «+ Tercera semana», igual que la anterior. */
  showThirdWeek = false;
  /** Muchas columnas (semana anterior o tercera desplegadas): modal ancho y fechas compactas. */
  dense = false;
  /** Fichas del eje sin camión (columna angosta): la leyenda de pipas las copia
   *  para verse IGUAL que los indicadores (Edey 12-oct). */
  tightChips = false;
  private currentRow: ProjectionRow | null = null;

  togglePrevWeek(): void {
    this.showPrevWeek = !this.showPrevWeek;
    if (this.currentRow) { this.draw(this.currentRow); }
  }

  toggleThirdWeek(): void {
    this.showThirdWeek = !this.showThirdWeek;
    if (this.currentRow) { this.draw(this.currentRow); }
  }

  /** La fila recortada a las dos semanas cuando la tercera está cerrada. */
  private shownDays(row: ProjectionRow): ProjectionRow {
    const n = row.twoWeekDays;
    if (this.showThirdWeek || n == null || n >= row.days.length) { return row; }
    return {
      ...row,
      days: row.days.slice(0, n),
      closes: row.closes.slice(0, n),
      confirmed: row.confirmed.slice(0, n),
      pending: row.pending.slice(0, n),
      suggested: row.suggested.slice(0, n),
    };
  }

  ngOnInit(): void {
    this.open();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['rows']?.firstChange || !changes['stationId']?.firstChange) {
      this.open();
    }
  }

  private open(): void {
    if (!this.rows?.length) { return; }
    const seen = new Set<string>();
    this.stationOptions = this.rows
      .filter((r) => !seen.has(r.stationId) && seen.add(r.stationId))
      .map((r) => ({ value: r.stationId, label: r.stationName || r.stationId }));
    const current = this.rows.find((r) => r.stationId === this.stationId && r.product === this.product);
    const first = current || this.rows.find((r) => r.stationId === this.stationId) || this.rows[0];
    this.station = first.stationId;
    this.selectedProduct = first.product;
    this.pick();
    // ngx-select: si las opciones y el valor llegan en el mismo ciclo puede
    // quedarse mostrando el placeholder; se reafirman en el siguiente tick.
    setTimeout(() => { this.station = first.stationId; this.selectedProduct = this.selectedProduct || first.product; });
  }

  /** Buscador de estación: sin acentos ni mayúsculas en los DOS lados. */
  stationSearch = (search: string, item: any): boolean => {
    const norm = (t: string) => (t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    return norm(item?.text || item?.data?.label || '').includes(norm(search));
  };

  pick(): void {
    const rows = this.rows.filter((r) => r.stationId === this.station);
    if (!rows.length) { return; }
    if (this.optionsStation !== this.station) {
      this.productOptions = rows.map((r) => ({ value: r.product, label: r.productLabel }));
      this.optionsStation = this.station;
    }
    const row = rows.find((r) => r.product === this.selectedProduct) || rows[0];
    if (this.selectedProduct !== row.product) { this.selectedProduct = row.product; }
    this.draw(row);
  }

  /** Control segmentado de producto (modo inline). */
  selectProduct(product: string): void {
    this.selectedProduct = product;
    this.pick();
  }

  close(): void {
    this.closed.emit();
  }

  /** Clic delegado sobre las fichas del eje (HTML de Highcharts): avisa a quien aloja la gráfica. */
  onChartClick(event: Event): void {
    const el = (event.target as HTMLElement)?.closest?.('.proj-x-chip--click') as HTMLElement | null;
    if (!el || !this.view?.row) { return; }
    const cls = Array.from(el.classList);
    const kind = (cls.find((c) => c.startsWith('pk-')) || '').slice(3) as ProjectionChipClick['kind'];
    const idx = +((cls.find((c) => c.startsWith('pi-')) || '').slice(3) || 0);
    const liters = +((el.textContent || '').replace(/[^\d]/g, '')) || 0;
    if (!kind) { return; }
    const pastCount = this.axisPast.length;
    const isPastOrToday = idx <= pastCount;
    this.chipClick.emit({
      kind, liters,
      stationId: this.view.row.stationId, product: this.view.row.product,
      pastOffset: isPastOrToday ? (idx === pastCount ? 0 : this.axisPast[idx].offset) : null,
      dayIndex: isPastOrToday ? null : idx - pastCount - 1,
    });
  }

  private draw(fullRow: ProjectionRow): void {
    this.currentRow = fullRow;
    const row = this.shownDays(fullRow);
    const f = (n: number) => new Intl.NumberFormat('es-MX').format(Math.round(n));
    const min = row.min || 0;
    const tope = row.tope || null;
    // Solo las reglas DEFINIDAS, tal cual (Edey 09-oct): la línea del mínimo (10 %)
    // y la línea de N días de venta (N × venta). Nada de niveles del motor ni de
    // sumas: eso vive en las sugerencias y en la columna Manda. Modo «days» no
    // tiene mínimo; modo «limits» no tiene días.
    const mode = row.ruleMode || 'both';
    const showMin = mode !== 'days' && !!min;
    // El nivel que manda el backend es el del motor (los N días se miden con la venta pronosticada de los días
    // que siguen); N × venta base solo es el respaldo cuando no viene.
    const ruleLiters = mode === 'limits' || row.ruleDays == null || row.objectiveRules
      ? null
      : row.ruleLevel != null ? Math.round(row.ruleLevel)
      : row.sale != null ? Math.round(row.ruleDays * row.sale) : null;
    const threshold = ruleLiters;
    // Obj. % (14-oct): a qué nivel deja el tanque la pipa sugerida. Sin captura no
    // hay línea; en modo «days» el motor lo ignora, igual que el mínimo y el tope.
    const target = mode !== 'days' && row.target ? row.target : null;
    // Gama del PRODUCTO de MOBICO (revisión 08-oct): inventario en el color base.
    // Nada de rojo ni verde en la gráfica: el producto puede ser rojo (Supreme) y
    // el verde con el azul "mata el ojo" (revisión 08-oct). La alerta la da el tono.
    const pal = (this.mobicoFlow ? null : GASPRE_PRODUCT_PALETTE[row.product]) || ({
      // pend = borde de la ficha Pendiente del Tablero (.pend-chip.mobico-*).
      regular: { base: '#0033a0', light: '#dbe4f7', faded: 'rgba(0,51,160,.35)', pend: '#3b6fd4' },
      premium: { base: '#c8102e', light: '#f8dde2', faded: 'rgba(200,16,46,.35)', pend: '#e0556b' },
      diesel: { base: '#3f4750', light: '#e0e3e6', faded: 'rgba(63,71,80,.35)', pend: '#6b7580' },
    } as Record<string, { base: string; light: string; faded: string; pend: string }>)[row.product]
      || { base: '#0033a0', light: '#dbe4f7', faded: 'rgba(0,51,160,.35)', pend: '#3b6fd4' };
    // Todo explícito en cada punto (color Y borde): si no, el punto hereda lo del
    // producto anterior al cambiar de selección. Tres lecturas (revisión 08-oct:
    // "lo proyectado con sugerencia tiene que concordar con la sugerencia"):
    // real = sólido; proyectado = relleno claro con borde (Edey 08-oct: el tono
    // medio "se podía perder" junto al real); proyectado que YA cuenta una pipa
    // sugerida = blanco con borde a rayas, igual que el chip de la sugerencia.
    const solid = (color: string) => ({ color, borderColor: color, borderWidth: 0, dashStyle: 'Solid' as Highcharts.DashStyleValue });
    const real = solid(pal.base);
    // Proyectado LISO en el tono claro del producto y SIN borde (2.ª revisión 12-oct:
    // con borde se confundía con la ficha Pendiente; a rayas, con «Bajo el mínimo»).
    // Real = tono base sólido; proyectado = el mismo color, más tenue.
    const projected = { color: pal.light, borderColor: pal.light, borderWidth: 0, dashStyle: 'Solid' as Highcharts.DashStyleValue };
    const withSuggested = { color: 'rgba(255,255,255,.7)', borderColor: pal.base, borderWidth: 1.5, dashStyle: 'Dash' as Highcharts.DashStyleValue };
    // Proyectado que YA cuenta un pedido PENDIENTE (Edey 15-oct: «no quiero que la
    // solicitud pendiente aparezca como teórico»): el tinte del producto con borde a rayas,
    // igual que la ficha Pendiente del Tablero (Edey: «el azul sombreado como la badge»).
    const withPending = { color: pal.light, borderColor: pal.pend, borderWidth: 1.5, dashStyle: 'Dash' as Highcharts.DashStyleValue };
    // Nivel que YA cuenta una pipa EN PROCESO (aprobada, sin llegada confirmada; Edey
    // 15-oct): contorno sólido del producto sobre blanco, como su ficha; no se pinta
    // como inventario real.
    const withInProcess = { color: 'rgba(255,255,255,.7)', borderColor: pal.base, borderWidth: 1.5, dashStyle: 'Solid' as Highcharts.DashStyleValue };
    // Bajo el mínimo (revisión 08-oct tarde: "no se distingue de proyectado"):
    // barra RAYADA en diagonal, rojo sobre blanco, con borde rojo. La alerta la
    // da la forma: en Supreme (producto rojo) un relleno rojo se fundía con las
    // barras normales. Sigue a rayas del borde si la barra lleva pipa sugerida.
    const hatch: any = {
      pattern: {
        path: { d: 'M 0 0 L 6 6 M -1 5 L 1 7 M 5 -1 L 7 1', strokeWidth: 1.5 },
        width: 6, height: 6, color: MIN_RED, backgroundColor: '#fff',
      },
    };
    const alert = { color: hatch, borderColor: MIN_RED, borderWidth: 1.5, dashStyle: 'Solid' as Highcharts.DashStyleValue };
    const alertSuggested = { color: hatch, borderColor: MIN_RED, borderWidth: 1.5, dashStyle: 'Dash' as Highcharts.DashStyleValue };
    // Revisión 08-oct ("tienes que mostrar nivel de inventario siempre"): UNA
    // sola serie = con cuánto cierra cada día, ya con las pipas de ese día. Las
    // pipas no se apilan: se leen como chip bajo la fecha (cuándo y de cuánto),
    // la misma fila de compras de la tabla.
    const stockSeries: Highcharts.PointOptionsObject[] = [];
    const pipes: { arrived: number; confirmed: number; pending: number; suggested: number }[] = [];
    const categories: string[] = [];
    const pushPoint = (value: number | null, isPast: boolean, suggested = false, pending = false, inProcess = false) => {
      // Manda lo más tentativo: sugerida > pendiente > en proceso > teórico; real solo sin pipa en proceso.
      const look = suggested ? withSuggested : pending ? withPending : inProcess ? withInProcess : isPast ? real : projected;
      const lookAlert = suggested ? alertSuggested : alert;
      const custom = (depleted: number) => ({ depleted, past: isPast, suggested, pending, inProcess });
      if (value == null) { stockSeries.push({ y: null, ...look, custom: custom(0) }); return; }
      // Agotado: sin barra (cero), el faltante solo en el tooltip. Fuera de MOBICO
      // (sin pipas sugeridas que lo levanten) el faltante se dibuja bajo el cero,
      // como el «−» en rojo de la tabla en Litros; si no, la gráfica queda vacía.
      if (value < 0) { stockSeries.push({ y: this.mobicoFlow ? 0 : value, ...lookAlert, custom: custom(-value) }); return; }
      stockSeries.push({ y: value, ...(value < min ? lookAlert : look), custom: custom(0) });
    };
    // Antier y ayer reales (si quien abre los trae), hoy y la proyección hasta
    // donde llega el motor.
    const allPast = row.past || [];
    const past = allPast.filter((p) => !p.prevWeek || this.showPrevWeek);
    this.axisPast = past.map((p, k) => ({ offset: p.offset ?? (past.length - k) }));
    // Contorno «en proceso» en el pasado SOLO cuando nada posterior lo confirma (Edey 16-oct:
    // «si hay un real después de una solicitud en proceso, que se marque en color de real»):
    //   · un día con pipa en proceso Y otra que ya llegó se pinta real (jefe 16-oct);
    //   · un día en proceso seguido de otro día con cierre (ayer en proceso, hoy con lectura)
    //     también se pinta real: el cierre posterior ya absorbió esa pipa. Garbri Santa Anna
    //     Supreme: 15,500 en proceso el 15 con el 16 real salía hueco entre dos sólidos.
    // Queda en proceso solo el último día con dato cuando su única pipa sigue sin confirmar.
    const inProcessOnly = (inProcess: any, arrived: any) => +(inProcess || 0) > 0 && !(+(arrived || 0) > 0);
    const hasToday = row.stockToday != null;
    const lastValued = hasToday ? past.length : past.map((p) => p.value != null).lastIndexOf(true);
    past.forEach((p, k) => {
      categories.push(p.label);
      pipes.push({ arrived: +(p.arrived || 0), confirmed: +(p.inProcess || 0), pending: 0, suggested: 0 });
      pushPoint(p.value != null ? Math.round(p.value) : null, true, false, false, k >= lastValued && inProcessOnly(p.inProcess, p.arrived));
    });
    categories.push(`Hoy<br>${moment().format('DD MMM')}`);
    pipes.push({ arrived: +(row.todayArrived || 0), confirmed: +(row.todayInProcess || 0), pending: 0, suggested: 0 });
    let inProcessSoFar = inProcessOnly(row.todayInProcess, row.todayArrived);
    pushPoint(row.stockToday != null ? Math.round(row.stockToday) : null, true, false, false, inProcessSoFar);
    let suggestedSoFar = false;
    let pendingSoFar = false;
    row.days.forEach((d, i) => {
      categories.push(`${d.dow}<br>${d.name}`);
      pipes.push({ arrived: 0, confirmed: +(row.confirmed[i] || 0), pending: +(row.pending[i] || 0), suggested: +(row.suggested[i] || 0) });
      // Desde el día en que entra una pipa sugerida (o un pedido pendiente), el nivel ya depende de ella.
      suggestedSoFar = suggestedSoFar || +(row.suggested[i] || 0) > 0;
      pendingSoFar = pendingSoFar || +(row.pending[i] || 0) > 0;
      inProcessSoFar = inProcessSoFar || +(row.confirmed[i] || 0) > 0;
      pushPoint(row.closes[i], false, suggestedSoFar, pendingSoFar, inProcessSoFar);
    });
    // Referencias SIN etiqueta (los valores van en la leyenda), DETRÁS de las
    // barras, con franjas tenues neutras. Mínimo en negro (revisión 08-oct).
    const plotLines: Highcharts.YAxisPlotLinesOptions[] = [];
    const plotBands: Highcharts.YAxisPlotBandsOptions[] = [];
    // Cada línea con su NOMBRE al extremo derecho y un trazo distinto (sólida /
    // a rayas / punteada): en la junta MOBICO "se perdían, no se sabía cuál era
    // cuál". El valor sigue en la leyenda.
    const refs: { value: number; color: string; width: number; text: string; dashStyle?: Highcharts.DashStyleValue }[] = [];
    const refLine = (value: number, color: string, width: number, text: string, dashStyle?: Highcharts.DashStyleValue) => {
      refs.push({ value, color, width, text, dashStyle });
    };
    // Mínimo en ROJO (el jefe no pidió negro, 08-oct tarde); la franja bajo el
    // mínimo apenas rojiza.
    if (showMin) {
      refLine(min, MIN_RED, 2, 'Mínimo');
      plotBands.push({ from: 0, to: min, color: 'rgba(205,33,32,.06)', zIndex: 1 });
    }
    if (threshold != null && threshold > 0) { refLine(threshold, '#4a4f55', 1.5, `Regla ${row.ruleDays} días`, 'Dash'); }
    if (target) { refLine(target, '#6c757d', 1.5, 'Objetivo', 'LongDashDot'); }
    if (tope) {
      refLine(tope, '#8c8c8c', 1.5, 'Tope', 'ShortDot');
      plotBands.push({ from: tope, to: Number.MAX_SAFE_INTEGER, color: 'rgba(0,0,0,.035)', zIndex: 1 });
    }
    // Líneas casi al mismo nivel (14-oct: «Regla» y «Objetivo» encimados; el
    // Objetivo topado queda IGUAL al Tope): en cada grupo de líneas juntas, la de
    // arriba lleva su nombre encima, la de abajo debajo y las de en medio a la
    // izquierda. «Juntas» = a menos de ~9 % del alto de la gráfica (≈ una etiqueta).
    const yTop = Math.max(tope || 0, ...refs.map((r) => r.value), ...stockSeries.map((pt) => +(pt.y || 0)), 1);
    const near = yTop * 0.09;
    const ordered = [...refs].sort((a, b) => b.value - a.value);
    const spots = [{ align: 'right', x: -2, y: -5 }, { align: 'right', x: -2, y: 12 }, { align: 'left', x: 2, y: -5 }, { align: 'left', x: 2, y: 12 }];
    let group: typeof ordered = [];
    const flush = () => {
      group.forEach((r, gi) => {
        // Uno solo: encima a la derecha (lo de siempre). En grupo: el de arriba
        // encima, el último debajo y los de en medio a la izquierda.
        const spot = group.length === 1 || gi === 0 ? spots[0]
          : gi === group.length - 1 ? spots[1]
          : spots[gi === 1 ? 2 : 3];
        plotLines.push({
          value: r.value, color: r.color, width: r.width, dashStyle: r.dashStyle, zIndex: 2,
          // HTML con fondo blanco (Edey 12-oct): en SVG las barras cortaban la
          // etiqueta («Reg|la 3 días», «Mínimo» encimado).
          // Hover con el valor (15-oct, pedido de MOBICO): el popup sale hacia el
          // lado contrario del rótulo (nunca arriba: el Tope va pegado al borde).
          label: {
            text: `<span class="proj-line-lab proj-line-lab--${spot.align}" style="color:${r.color}">${r.text}`
              + `<span class="proj-line-lab__pop">${r.text}: <b>${f(r.value)} lts</b></span></span>`,
            useHTML: true, align: spot.align as Highcharts.AlignValue, x: spot.x, y: spot.y,
            // Sin `textOverflow: ellipsis` (default de Highcharts): con él envuelve el
            // rótulo en un span con overflow:hidden y recorta el popup del hover.
            style: { textOverflow: 'clip' },
          },
        });
      });
      group = [];
    };
    ordered.forEach((r) => {
      if (group.length && group[group.length - 1].value - r.value >= near) { flush(); }
      group.push(r);
    });
    flush();
    // Chip de pipa bajo la fecha: MISMAS formas que el calendario (jefe 09-oct:
    // "lo mismo que viene en Cronograma se tiene que ver aquí"): llegó = relleno ·
    // en proceso = contorno sólido · pendiente = tinte claro punteado · sugerida =
    // a rayas con «+». Todo en la gama del producto.
    const chip = (liters: number, kind: 'arrived' | 'confirmed' | 'pending' | 'suggested', idx: number) => {
      if (!liters) { return ''; }
      const style = kind === 'arrived' ? `background:${pal.base};color:#fff`
        // Pendiente en gris claro (2.ª revisión 12-oct: con el tinte del producto se
        // confundía con las barras Proyectado). Borde a rayas del color del producto.
        : kind === 'pending' ? `background:${pal.light};border-color:${pal.pend};color:${pal.base}`
        : `color:${pal.base}`;
      // Píldora con camión, igual que el chip del calendario (Edey 12-oct: «iguala los chips»).
      // Tipo e índice van en CLASES (pk-/pi-): el HTML de Highcharts pasa por su AST y
      // descarta los atributos data-*.
      return `<span class="proj-x-chip proj-x-chip--${kind} proj-x-chip--click pk-${kind} pi-${idx}" style="border-color:${pal.base};${style}"><i class="fa fa-truck" aria-hidden="true"></i>${kind === 'suggested' ? '+' : ''}${f(liters)}</span>`;
    };
    // Con la semana anterior desplegada son ~25 columnas: todo debe caber sin
    // scroll (Edey 09-oct: "el chiste es que se vea toda"): el modal se ensancha
    // (proj-modal--wide) y las fechas van compactas (sin puntos, 10px).
    const dense = categories.length > 16;
    this.dense = dense;
    const axisLabelStyle: Highcharts.CSSObject = { color: '#8c8c8c', fontSize: dense ? '10px' : '11px', fontWeight: '500' };
    const pastCount = past.length;
    // Con faltante bajo el cero (fuera de MOBICO), la línea del cero separa lo que hay de lo que falta.
    if (!this.mobicoFlow && stockSeries.some((p) => +(p.y || 0) < 0)) {
      plotLines.push({ value: 0, color: '#8c8c8c', width: 1, zIndex: 3 });
    }
    this.options = {
      chart: {
        type: 'column', height: 300, spacing: [12, 8, 4, 4], style: { fontFamily: 'inherit' }, animation: false,
        events: {
          // El ancho real de cada fecha solo se conoce al dibujar: avisa a la leyenda si
          // las fichas del eje van compactas (fuera del ciclo de detección de cambios).
          render: (ev: any) => {
            const ch = ev?.target;
            const w = (ch?.plotWidth || 0) / Math.max(categories.length, 1);
            const tight = w > 0 && w < 72;
            if (tight !== this.tightChips) { setTimeout(() => { this.tightChips = tight; }); }
          },
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories, gridLineWidth: 0, tickLength: 0, lineColor: '#dee2e6',
        labels: {
          useHTML: true, style: axisLabelStyle, rotation: 0, autoRotation: undefined, step: 1,
          formatter: function () {
            const i = this.pos;
            const p = pipes[i] || { arrived: 0, confirmed: 0, pending: 0, suggested: 0 };
            const chips = chip(p.arrived, 'arrived', i) + chip(p.confirmed, 'confirmed', i) + chip(p.pending, 'pending', i) + chip(p.suggested, 'suggested', i);
            const text = dense ? String(this.value).replace(/\./g, '') : String(this.value);
            // Columna angosta (pantalla chica o muchas fechas): la ficha va sin camión para
            // no encimarse con la del día siguiente (Edey 12-oct). Número y forma se quedan.
            const colWidth = (this.chart?.plotWidth || 0) / Math.max(categories.length, 1);
            const tight = colWidth > 0 && colWidth < 72;
            return `<div class="proj-x-lab${i < pastCount + 1 ? ' proj-x-lab--real' : ''}${dense ? ' proj-x-lab--dense' : ''}${tight ? ' proj-x-lab--tight' : ''}"><span>${text}</span>${chips ? `<span class="proj-x-pipes">${chips}</span>` : ''}</div>`;
          },
        },
        // Separa lo real (Antier · Ayer · Hoy) de lo proyectado.
        plotLines: pastCount || row.days.length ? [{ value: pastCount + .5, color: '#dee2e6', width: 1, dashStyle: 'Dash', zIndex: 1 }] : [],
      },
      yAxis: {
        title: { text: null }, min: this.mobicoFlow ? 0 : null, gridLineDashStyle: 'Dash', gridLineColor: '#dee2e6', lineWidth: 0,
        labels: { style: axisLabelStyle, formatter: function () {
          // Compacto también bajo el cero (faltante fuera de MOBICO): −100k, no -100000.
          const v = this.value as number;
          const sign = v < 0 ? '−' : '';
          return Math.abs(v) >= 1000 ? `${sign}${Math.round(Math.abs(v) / 1000)}k` : `${sign}${Math.abs(v)}`;
        } },
        plotLines,
        plotBands,
        softMax: tope || undefined,
      },
      plotOptions: {
        column: { borderWidth: 0, pointPadding: 0.1, groupPadding: 0.08, borderRadius: 2 },
        series: { animation: false, states: { inactive: { opacity: 1 } } },
      },
      tooltip: {
        useHTML: true, backgroundColor: '#333c44', borderWidth: 0, borderRadius: 6, style: { color: '#fff', fontSize: '12px' },
        formatter: function () {
          const pt: any = this.point;
          const p = pipes[pt.index] || { arrived: 0, confirmed: 0, pending: 0, suggested: 0 };
          const depleted = pt?.custom?.depleted;
          const label = pt?.custom?.past ? 'Inventario' : pt?.custom?.suggested ? 'Cierre del día (si se pide la sugerida)' : pt?.custom?.pending ? 'Cierre del día (con el pedido pendiente)' : pt?.custom?.inProcess ? 'Cierre del día (con la pipa en proceso)' : 'Cierre del día';
          const value = depleted ? `agotado · faltan ${f(depleted)} lts` : `${f(pt.y)} lts`;
          const lines = [`<div>${label}: <b>${value}</b></div>`];
          if (p.arrived) { lines.push(`<div>Pipa que llegó: <b>${f(p.arrived)} lts</b></div>`); }
          if (p.confirmed) { lines.push(`<div>Pipa en proceso: <b>${f(p.confirmed)} lts</b></div>`); }
          if (p.pending) { lines.push(`<div>Pipa pendiente: <b>${f(p.pending)} lts</b></div>`); }
          if (p.suggested) { lines.push(`<div>Pipa sugerida: <b>+${f(p.suggested)} lts</b></div>`); }
          return `<div><b>${String(this.x).replace('<br>', ' ')}</b></div>${lines.join('')}`;
        },
      },
      series: [
        { type: 'column', name: 'Inventario', data: stockSeries, color: pal.base },
      ],
    } as Highcharts.Options;
    this.view = {
      row, pal, min: showMin ? min : 0, tope, threshold, target,
      ruleDays: row.ruleDays, sale: row.sale, stock: row.stockToday, daysLeft: row.daysLeft,
      hasArrived: pipes.some((v) => !!v.arrived), hasConfirmed: pipes.some((v) => !!v.confirmed), hasPending: pipes.some((v) => !!v.pending), hasSuggested: pipes.some((v) => !!v.suggested),
      hasProjected: row.days.length > 0,
      hasPrevWeek: allPast.some((p) => !!p.prevWeek),
      hasThirdWeek: fullRow.twoWeekDays != null && fullRow.days.length > fullRow.twoWeekDays,
      hasShortfall: !this.mobicoFlow && stockSeries.some((p) => +(p.y || 0) < 0),
    };
  }
}
